// Обновлённая страница дашборда с интеграцией Wildberries
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart3,
  CheckCircle,
  AlertTriangle,
  Shield,
  RefreshCw,
  Download,
  Package,
  TrendingUp,
  Bell
} from 'lucide-react'
import { useWbToken } from '../hooks/useWbToken'
import { useWildberries } from '../hooks/useWildberries'
import { useCheckProduct } from '../hooks/useCheckProduct'
import { LoadingOverlay } from '../components/ui/LoadingOverlay'
import Header from '../components/layout/Header'
import { WBConnection } from '../components/wildberries/WBConnection'
import { ProductList } from '../components/wildberries/ProductList'
import { ComplianceReport } from '../components/wildberries/ComplianceReport'
import { RiskBadge, ComplianceScoreBadge } from '../components/wildberries/RiskBadge'
import { toast } from 'sonner'

const Dashboard = () => {
  const { isConnected } = useWbToken()
  const navigate = useNavigate()
  const {
    products,
    checks,
    setChecks,
    loading,
    error,
    verifyConnection,
    fetchProducts,
    checkBatch
  } = useWildberries()
  const {
    check: checkProductWithProgress,
    loading: checkLoading,
    error: checkError,
    result: checkResult,
    progress,
    lastCheckedNmId
  } = useCheckProduct()

  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([])
  const [showReportModal, setShowReportModal] = useState(false)
  const [selectedReport, setSelectedReport] = useState<string | null>(null)
  const [batchLoading, setBatchLoading] = useState(false)
  const [batchProgress, setBatchProgress] = useState('')
  const [stats, setStats] = useState({
    totalProducts: 0,
    checkedProducts: 0,
    highRiskProducts: 0,
    complianceScore: 0
  })

  // Проверка подключения при загрузке (только если токен сохранён)
  useEffect(() => {
    if (isConnected) {
      verifyConnection()
    }
  }, [isConnected])

  // Обновление статистики при изменении продуктов и проверок
  useEffect(() => {
    console.log('🔍 Dashboard state:', {
      productsCount: products?.length,
      checksKeys: Object.keys(checks || {}),
      firstCheck: checks ? Object.values(checks)[0] : undefined,
      isConnected,
      loading,
    });
    // ✅ Защита: если checks не объект — выходим
    if (!checks || typeof checks !== 'object') return;
    
    const checksArray = Object.values(checks).filter((c: any) => !!c); // ✅ Фильтруем undefined
    
    const checkedCount = checksArray.length;
    
    const highRiskCount = checksArray.filter((check: any) => 
      check?.high_severity_risks?.length > 0  // ✅ Опциональная цепочка
    ).length;
    
    const avgScore = checkedCount > 0
      ? checksArray.reduce((sum, check: any) => {
          // ✅ Безопасное извлечение value (число или объект)
          const value = typeof check?.compliance_score === 'object' 
            ? check.compliance_score?.value 
            : check?.compliance_score;
          return sum + (value || 0);
        }, 0) / checkedCount
      : 0;

    setStats({
      totalProducts: products?.length || 0,  // ✅ Защита для products
      checkedProducts: checkedCount,
      highRiskProducts: highRiskCount,
      complianceScore: avgScore || 0
    });
  }, [products, checks, isConnected, loading])  // ✅ Зависимости остались те же

  // Синхронизация результата проверки в общее состояние checks
  // Используем lastCheckedNmId из хука, т.к. бэкенд не возвращает nm_id
  useEffect(() => {
    if (checkResult && lastCheckedNmId) {
      setChecks(prev => ({
        ...prev,
        [lastCheckedNmId]: {
          ...checkResult,
          nm_id: lastCheckedNmId
        }
      }))
    }
  }, [checkResult, lastCheckedNmId, setChecks])

  // Отслеживаем завершение проверки для показа toast
  const prevCheckLoading = useRef(checkLoading)

  useEffect(() => {
    // Если loading изменился с true на false — проверка завершена
    if (prevCheckLoading.current && !checkLoading) {
      if (checkError) {
        toast.error(`Ошибка проверки: ${checkError}`)
      } else if (checkResult) {
        toast.success('✅ Проверка успешно завершена!')
      }
    }
    prevCheckLoading.current = checkLoading
  }, [checkLoading, checkError, checkResult])

  // Обработчики действий
  const handleLoadProducts = async () => {
    const loaded = await fetchProducts(50) // лимит по умолчанию
    if (loaded.length > 0) {
      toast.success(`Загружено ${loaded.length} товаров`)
    }
  }

  const handleCheckSelected = async () => {
    if (selectedProductIds.length === 0) {
      toast.warning('Выберите товары для проверки')
      return
    }

    setBatchLoading(true)
    const total = selectedProductIds.length
    toast.info(`Начинаю проверку ${total} товаров...`)

    try {
      for (let i = 0; i < selectedProductIds.length; i++) {
        const nmId = selectedProductIds[i]
        const product = products.find(p => p?.nm_id === nmId)
        if (product?.nm_id) {
          setBatchProgress(`Проверяю ${i + 1} из ${total}: ${product.name || nmId}`)
          await checkProductWithProgress(
            product.description || '',
            { brand: product.brand, category: product.category, nm_id: product.nm_id },
            String(product.nm_id)
          )
        }
      }
      toast.success(`Проверка ${total} товаров завершена`)
    } catch (err) {
      toast.error('Ошибка при проверке товаров')
    } finally {
      setBatchLoading(false)
      setBatchProgress('')
    }
  }

  const handleCheckBatch = async () => {
    const result = await checkBatch(10)
    toast.success(`Проверено ${result.length} товаров`)
  }

  const handleViewReport = async (nmId: string) => {
    console.log('🔍 handleViewReport:', {
      nmId,
      checksKeys: Object.keys(checks),
      checkData: checks[nmId]
    })

    if (!checks[nmId]) {
      toast.error('Отчёт не найден. Попробуйте проверить товар заново.')
      return
    }

    setSelectedReport(nmId)
    setShowReportModal(true)
  }

  const handleRecheck = async (nmId: string) => {
    const product = products.find(p => p?.nm_id === nmId)
    if (product?.nm_id) {
      setBatchLoading(true)
      setBatchProgress(`Проверяю: ${product.name || nmId}`)
      try {
        await checkProductWithProgress(
          product.description || '',
          { brand: product.brand, category: product.category, nm_id: product.nm_id },
          String(product.nm_id)
        )
        toast.success('Повторная проверка завершена')
      } catch (err) {
        toast.error('Ошибка при проверке')
      } finally {
        setBatchLoading(false)
        setBatchProgress('')
      }
    }
  }

  // Статистические карточки
  const statCards = [
    {
      title: 'Всего товаров',
      value: stats.totalProducts,
      icon: <Package className="text-blue-600" size={24} />,
      color: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    {
      title: 'Проверено',
      value: stats.checkedProducts,
      icon: <CheckCircle className="text-green-600" size={24} />,
      color: 'bg-green-50',
      textColor: 'text-green-700'
    },
    {
      title: 'Высокий риск',
      value: stats.highRiskProducts,
      icon: <AlertTriangle className="text-red-600" size={24} />,
      color: 'bg-red-50',
      textColor: 'text-red-700'
    },
    {
      title: 'Средний балл',
      value: `${Math.round(stats.complianceScore * 100)}%`,
      icon: <TrendingUp className="text-purple-600" size={24} />,
      color: 'bg-purple-50',
      textColor: 'text-purple-700'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {(batchLoading || checkLoading) && (
        <LoadingOverlay progress={batchProgress || progress} />
      )}
      
      {/* Ошибка проверки */}
      {checkError && (
        <div className="fixed top-4 right-4 max-w-md z-50">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-600 mt-0.5 shrink-0" size={20} />
              <div>
                <p className="font-medium text-red-800">Ошибка проверки</p>
                <p className="text-red-700 text-sm mt-1">{checkError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Приветствие и статус подписки */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Добрый день!
              </h1>
              <p className="text-gray-600 mt-1">
                Автоматическая проверка карточек Wildberries на соответствие оферте
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm">
                <div className="w-3 h-3 rounded-full mr-2 bg-green-500" />
                <span className="text-sm font-medium text-gray-700">
                  Проверки без ограничений
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Статус подключения WB */}
        <div className="mb-8">
          {!isConnected ? (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Подключите Wildberries</h2>
                <RiskBadge level="pending" label="Не подключено" />
              </div>
              <WBConnection 
                onConnected={() => {
                  verifyConnection()
                  toast.success('Аккаунт подключён! Теперь вы можете загружать товары.')
                }}
              />
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="text-green-600" size={28} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Wildberries подключён</h3>
                    <p className="text-gray-600">Токен активен, можно загружать товары</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleLoadProducts}
                    disabled={loading}
                    className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <RefreshCw size={18} />
                    Обновить товары
                  </button>
                  <button
                    onClick={verifyConnection}
                    className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                  >
                    Проверить подключение
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Статистика */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, index) => (
            <div
              key={index}
              className={`${card.color} rounded-xl p-6 border border-transparent hover:border-gray-200 transition-colors`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className={`text-3xl font-bold mt-2 ${card.textColor}`}>{card.value}</p>
                </div>
                {card.icon}
              </div>
            </div>
          ))}
        </div>

        {/* Быстрые действия */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Действия</h2>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Bell size={16} />
              <span>Автоматическая проверка каждые 24 часа</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleCheckBatch}
              disabled={loading || batchLoading || checkLoading || products.length === 0}
              className="p-6 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <Shield className="text-indigo-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Проверить все карточки</h3>
                  <p className="text-sm text-gray-600 mt-1">Запуск RAG-проверки для всех товаров</p>
                </div>
              </div>
            </button>

            <button
              onClick={handleLoadProducts}
              disabled={loading}
              className="p-6 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Download className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Загрузить новые товары</h3>
                  <p className="text-sm text-gray-600 mt-1">Синхронизация с Wildberries API</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/history')}
              className="p-6 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all text-left"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <BarChart3 className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Статистика и отчёты</h3>
                  <p className="text-sm text-gray-600 mt-1">Детальная аналитика и экспорт</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Список товаров */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Карточки товаров</h2>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {selectedProductIds.length > 0 && (
                  <span className="font-medium">Выбрано: {selectedProductIds.length}</span>
                )}
              </div>
              <button
                onClick={handleCheckSelected}
                disabled={selectedProductIds.length === 0 || loading || batchLoading || checkLoading}
                className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Проверить выбранные
              </button>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <Package className="mx-auto text-gray-400" size={64} />
              <h3 className="text-xl font-semibold text-gray-900 mt-6">Товары не загружены</h3>
              <p className="text-gray-600 mt-2 max-w-md mx-auto">
                {isConnected
                  ? 'Нажмите "Обновить товары" чтобы загрузить карточки с Wildberries'
                  : 'Подключите аккаунт Wildberries чтобы начать работу'}
              </p>
              {isConnected && (
                <button
                  onClick={handleLoadProducts}
                  className="mt-6 px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
                >
                  Загрузить товары
                </button>
              )}
            </div>
          ) : (
            <ProductList
              products={products}
              checks={checks}
              onSelect={setSelectedProductIds}
              onCheck={async (nmId) => {
                const product = products.find(p => p.nm_id === nmId)
                if (product) {
                  setBatchLoading(true)
                  setBatchProgress(`Проверяю: ${product.name || nmId}`)
                  try {
                    await checkProductWithProgress(
                      product.description || '',
                      { brand: product.brand, category: product.category, nm_id: product.nm_id },
                      String(product.nm_id)
                    )
                  } catch (err) {
                    toast.error('Ошибка при проверке')
                  } finally {
                    setBatchLoading(false)
                    setBatchProgress('')
                  }
                }
              }}
              onViewReport={handleViewReport}
              loading={loading}
              checkLoading={batchLoading || checkLoading}
            />
          )}
        </div>

        {/* Последние проверки */}
        {Object.keys(checks).length > 0 && (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 mb-6">Последние проверки</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Object.values(checks)
        .slice(0, 6)
        .map((check: any) => {  // ✅ Добавь : any если нет типов
          // ✅ МИНИМАЛЬНАЯ ЗАЩИТА: пропускаем, если нет nm_id
          if (!check?.nm_id) return null;
          
          // ✅ Безопасный поиск с опциональной цепочкой
          const product = products.find((p: any) => p?.nm_id === check.nm_id);
          
          // ✅ Безопасное извлечение score (число или объект)
          const scoreValue = typeof check.compliance_score === 'object' 
            ? check.compliance_score?.value 
            : check.compliance_score;
          
          // ✅ Безопасное извлечение summary (строка или объект)
          const summaryShort = typeof check.summary === 'object'
            ? check.summary?.short 
            : (check.summary?.slice?.(0, 140) || 'Нет описания');

          return (
            <div
              key={check.nm_id}
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:border-indigo-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {product?.name || `Товар #${check.nm_id}`}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Артикул: {check.nm_id}</p>
                </div>
                <ComplianceScoreBadge score={scoreValue || 0} size="sm" />
              </div>
              
              <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                {summaryShort}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {check.high_severity_risks?.length > 0 ? (
                    <>
                      <AlertTriangle className="text-red-500" size={16} />
                      <span className="text-sm text-red-600">
                        {check.high_severity_risks.length} высоких рисков
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="text-green-500" size={16} />
                      <span className="text-sm text-green-600">Рисков нет</span>
                    </>
                  )}
                </div>
                <button
                  onClick={() => handleViewReport(check.nm_id)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Подробнее →
                </button>
              </div>
            </div>
          );
        })}
    </div>
  </div>
)}
      </main>

      {/* Модалка с отчётом */}
      {showReportModal && selectedReport && checks[selectedReport] && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Отчёт по проверке</h2>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  ✕
                </button>
              </div>
              
              {(() => {
                const check = checks[selectedReport]
                // ✅ Проверка, что check существует и имеет nm_id
                if (!check?.nm_id) {
                  return (
                    <div className="text-center py-12">
                      <p className="text-gray-600">Отчёт не найден</p>
                    </div>
                  )
                }
                const product = products.find(p => p?.nm_id === check.nm_id)
                return (
                  <ComplianceReport
                    check={check}
                    productName={product?.name}  // ✅ опциональная цепочка
                    onRecheck={() => handleRecheck(check.nm_id)}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Обработка ошибок */}
      {error && (
        <div className="fixed bottom-4 right-4 max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-600 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-red-800">Ошибка</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="text-red-700 hover:text-red-900 text-sm font-medium"
              >
                Обновить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard